import React, { useCallback, useEffect, useRef, useState } from 'react';
import ProductCard from '../common/ProductCard';
import { ProductGridSkeleton } from '../common/SkeletonLoader';
import { getDiscoveryFeed } from '../../services/productService';

/**
 * Infinite-scroll discovery feed for the homepage.
 *
 * Paging is cursor-based against /api/products/feed, whose ordering interleaves
 * every leaf category (see backend/utils/discoveryFeed). The buyer therefore
 * passes through every category within the first few pages and sees every
 * product before anything repeats.
 *
 * `categoryLevel1` scopes the feed to the homepage's gender toggle. The backend
 * holds a separate ordering per Level-1, so a cursor only means anything within
 * one — switching gender starts a fresh feed rather than continuing the old.
 *
 * Once the whole catalogue has been shown the feed wraps and keeps going, which
 * is the requested "scroll everything, then loop" behaviour. The wrap is
 * announced rather than silent, so repeats don't read as a bug.
 */
const PAGE_SIZE = 20;

const DiscoveryFeed = ({ onAddToCart, addingToCart = {}, categoryLevel1 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ total: 0, cycle: 0, totalCategories: 0 });
  const [wrapped, setWrapped] = useState(false);

  const sentinelRef = useRef(null);
  // Ref rather than state: the IntersectionObserver callback closes over its
  // creation-time scope, so reading `loading` from state there goes stale and
  // fires a burst of duplicate requests.
  const loadingRef = useRef(false);
  const cursorRef = useRef(0);
  // Bumped on every reset. A request started before a gender switch resolves
  // against the old ordering, so its products must not land in the new feed.
  const requestIdRef = useRef(0);

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const requestId = requestIdRef.current;
    try {
      const res = await getDiscoveryFeed({
        cursor: cursorRef.current,
        limit: PAGE_SIZE,
        categoryLevel1
      });
      if (requestId !== requestIdRef.current) return;

      if (res?.success) {
        const batch = res.data || [];
        const pagination = res.pagination || {};

        setItems((prev) => [...prev, ...batch]);
        cursorRef.current = pagination.nextCursor ?? cursorRef.current + PAGE_SIZE;
        setMeta({
          total: pagination.total || 0,
          cycle: pagination.cycle || 0,
          totalCategories: pagination.totalCategories || 0
        });
        if (pagination.cycle > 0) setWrapped(true);
      } else {
        setError('Could not load more products');
      }
    } catch (e) {
      console.error('Discovery feed error:', e);
      if (requestId === requestIdRef.current) setError('Could not load more products');
    } finally {
      // The reset already cleared the flag for the new feed; clearing it again
      // from a stale request would let two pages load at once.
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [categoryLevel1]);

  // Start over when the gender changes, or the previous gender's products stay
  // on screen above the new ones. Declared before the fetch effect so the reset
  // lands first when both run in the same commit.
  useEffect(() => {
    requestIdRef.current += 1;
    loadingRef.current = false;
    cursorRef.current = 0;
    setItems([]);
    setWrapped(false);
    setMeta({ total: 0, cycle: 0, totalCategories: 0 });
  }, [categoryLevel1]);

  // First page on mount
  useEffect(() => { loadMore(); }, [loadMore]);

  // Load the next page as the sentinel nears the viewport. rootMargin starts
  // the fetch before the user hits the bottom so scrolling stays continuous.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '600px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="px-4 py-4">
      <div className="mb-3">
        <h3 className="text-base font-bold text-gray-900">Explore Everything</h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {items.map((product, i) => (
          // The id repeats after the feed wraps, so it can't be the key alone.
          <ProductCard
            key={`${product._id}-${i}`}
            product={product}
            onAddToCart={onAddToCart}
            isAddingToCart={addingToCart[product._id]}
            showAddToCart={false}
          />
        ))}
      </div>

      {loading && (
        <div className="mt-3">
          <ProductGridSkeleton count={4} cols="grid-cols-2" />
        </div>
      )}

      {/* Shown the first time the catalogue is exhausted, so a shopper knows
          the repeats below are a fresh pass and not a glitch. */}
      {wrapped && !loading && (
        <div className="py-5 text-center">
          <p className="text-xs font-semibold text-gray-600">
            You&apos;ve seen all {meta.total.toLocaleString()} products
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">Starting again from the top</p>
        </div>
      )}

      {error && (
        <div className="py-6 text-center">
          <p className="text-sm text-gray-500 mb-2">{error}</p>
          <button
            onClick={loadMore}
            className="text-xs font-semibold text-blue-500 hover:text-blue-600"
          >
            Try again
          </button>
        </div>
      )}

      {/* Observed sentinel — keep it rendered so the observer has a target. */}
      <div ref={sentinelRef} className="h-px" aria-hidden="true" />
    </div>
  );
};

export default DiscoveryFeed;
